import React from "react";
import { LibraryPhoto } from "../../PhotoManager/API/api";
import { CartCheckMedia } from "../../PhotoManager/styles/CartCheckMedia";
import PhotoVisibilityIcon from "../../PhotoManager/components/PhotoVisibilityIcon";
import { EventGroup } from "./types";

interface PhotoGridProps {
  eventGroups: EventGroup[];
  selectedIds: Set<number>;
  hiddenIds: Set<number>;
  togglePhotoSelection: (photoId: number) => void;
  handlePhotoClick: (
    photo: LibraryPhoto,
    albumPhotos: LibraryPhoto[],
    indexInAlbum: number
  ) => void;
  navigateToEvent: (eventId: number) => void;
  navigateToAlbum: (eventId: number, albumId: number) => void;
  showActionTooltip: (id: number, title: string, type: string) => void;
}

const PhotoGrid: React.FC<PhotoGridProps> = ({
  eventGroups,
  selectedIds,
  hiddenIds,
  togglePhotoSelection,
  handlePhotoClick,
  navigateToEvent,
  navigateToAlbum,
  showActionTooltip
}) => {
  const isPhotoHidden = (id: number) => hiddenIds.has(id);

  return (
    <>
      {eventGroups.map(event =>
        event.albums.map(album => (
          <div
            key={`${event.eventId}-${album.albumId}`}
            className="filtered-album-section"
          >
            <div className="filtered-album-header">
              <div className="filtered-album-title">
                <span
                  className="filtered-event-name"
                  onClick={() => navigateToEvent(event.eventId)}
                >
                  {event.eventName}
                </span>
                <i className="fa fa-angle-right filtered-title-separator"></i>
                <span
                  className="filtered-album-name-title filtered-album-name-title--link"
                  onClick={() =>
                    navigateToAlbum(event.eventId, album.albumId)
                  }
                >
                  {album.albumName}
                </span>
              </div>
              <div className="filtered-album-path">
                Library
                {album.photos[0]?.folderPath?.length > 0 && (
                  <> / {album.photos[0].folderPath.join(" / ")}</>
                )}
                {" / "}
                {event.eventName} /
              </div>
            </div>

            <div className="ws-photo-grid">
              {album.photos.map((photo, idx) => {
                const isSelected = selectedIds.has(photo.id);
                const isHidden = isPhotoHidden(photo.id);
                return (
                  <div
                    key={photo.id}
                    className={`ws-grid-item${
                      isSelected ? " selected" : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      handlePhotoClick(photo, album.photos, idx)
                    }
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handlePhotoClick(photo, album.photos, idx);
                      }
                    }}
                    aria-label={`View photo ${photo.fileName}`}
                  >
                    <button
                      type="button"
                      className="ws-check-icon-container"
                      onClick={e => {
                        e.stopPropagation();
                        togglePhotoSelection(photo.id);
                      }}
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          togglePhotoSelection(photo.id);
                        }
                      }}
                      aria-label={
                        isSelected
                          ? `Deselect photo ${photo.fileName}`
                          : `Select photo ${photo.fileName}`
                      }
                      aria-pressed={isSelected}
                    >
                      <CartCheckMedia
                        color={isSelected ? "#259fa5" : "#5d5d5d70"}
                        tickMarkColor={isSelected ? "white" : "#cbcbcb"}
                      />
                    </button>

                    <PhotoVisibilityIcon
                      photo={{
                        ...photo,
                        hidden: isHidden,
                        redacted: photo.permissions?.redacted ?? false,
                        redactionChecked: photo.redactionChecked,
                        flaggedForModeration: photo.flaggedForModeration ?? false
                      }}
                      showActionTooltip={showActionTooltip}
                    />

                    {photo.videoContent && (
                      <div className="filtered-video-indicator">
                        <i className="fa fa-play-circle"></i>
                      </div>
                    )}

                    <img
                      className="ws-thumb-photo"
                      src={photo.thumbnailURL}
                      alt={photo.fileName}
                      loading="lazy"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </>
  );
};

export default PhotoGrid;
